import { useState } from 'react';

import { Page, PageHeader } from '../components/layout/AppShell';
import { Card } from '../components/ui/Card';
import { DataTable, TableToolbar, Pagination } from '../components/ui/DataTable';
import { AsyncBoundary, EmptyState, SkeletonRows } from '../components/ui/AsyncBoundary';
import { SearchInput } from '../components/ui/Field';
import { Button } from '../components/ui/Button';
import { auditColumns } from '../components/domain/columns';
import { usePagedList } from '../hooks/useResource';

/**
 * The audit log.
 *
 * Append-only on the server: no route updates or deletes a row. A manager or
 * viewer sees only their own actions, which the API decides — this page does
 * no filtering of its own beyond the search box.
 */
export function Audit() {
  // Paged rather than capped. This is the table that grows without limit — one
  // row per action by every person on the platform, kept for years — so
  // fetching "the last 300" and calling it the log is how an investigation
  // silently misses the entry it was looking for.
  const [action, setAction] = useState('');
  const entries = usePagedList('/api/audit', { action: action || undefined }, { limit: 100 });

  return (
    <Page className="space-y-4">
      <PageHeader
        title="Audit log"
        subtitle="Who did what, to what, and when"
        actions={
          <Button variant="secondary" size="sm" icon="refresh" onClick={entries.refetch}>
            Refresh
          </Button>
        }
      />

      <Card className="overflow-hidden">
        <TableToolbar>
          <SearchInput
            value={action}
            onChange={setAction}
            placeholder="Filter by action, e.g. device."
            className="w-full sm:w-72"
          />
          <span className="ml-auto text-[12.5px] text-ink-3 tabular-nums">
            {entries.total.toLocaleString()} entries
          </span>
        </TableToolbar>

        <AsyncBoundary
          state={entries}
          skeleton={<SkeletonRows rows={10} />}
          isEmpty={() => !entries.rows.length}
          empty={
            <EmptyState
              icon="shield"
              title="Nothing recorded yet"
              description="Sign-ins, permission changes and device actions all land here."
            />
          }
        >
          {() => (
            <>
              <DataTable
                columns={auditColumns()}
                rows={entries.rows}
                rowKey={(row) => row.id}
                dense
                defaultSort={{ key: 'at', direction: 'desc' }}
                paged={entries.total > entries.page.limit}
              />
              <Pagination page={entries.page} onChange={entries.setOffset} unit="entries" />
            </>
          )}
        </AsyncBoundary>
      </Card>
    </Page>
  );
}
